import { useQuery } from "@tanstack/react-query";
import { IndividualSavingsTabStatus } from "features/individuals-savings/consts";
import { useParams, useSearchParams } from "react-router-dom";
import { getTodayAsEndDateFilterValue } from "utils";
import { ZERO } from "utils/constants";

import { DEFAULT_PAGINATION_LIMIT, PAGE_ONE } from "ui/pagination";

import {
  getIndividualSavingsCount,
  getIndividualVaultSavings,
  getIndividualVaultSavingsDetails,
  getIndividualVaultSavingsStats,
  getIndividualVaultSavingsTransactions,
  getIndividualVaultSavingsTransactionsStats,
} from ".";
import { IndividualSavingsQueryKeys } from "./consts";
import {
  IGetIndividualVaultSavingsDetailsPayload,
  IGetIndividualVaultSavingsPayload,
  IGetIndividualVaultSavingsTransactionsPayload,
  IGetIndividualVaultSavingsTransactionsStatsPayload,
  IIndividualVaultSavingsStatsPayload,
} from "./types";

// Query hooks
export const useGetIndividualVaultSavingsStats = () => {
  const { savingsType } = useParams<{
    savingsType: IndividualSavingsTabStatus;
  }>();
  const [searchParams] = useSearchParams();
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");
  const isDateFilter = searchParams.get("isDateFilter");
  const resetFilter = searchParams.get("resetFilter");
  const payload: IIndividualVaultSavingsStatsPayload = {};

  if (isDateFilter && isDateFilter === "true") {
    if (startDate) {
      payload["startDate"] = startDate;
    }

    if (endDate) {
      payload["endDate"] = endDate;
    }
  }

  const queryKeys = [
    IndividualSavingsQueryKeys.GET_INDIVIDUAL_VAULT_SAVINGS_STATS,
    payload?.startDate,
    payload?.endDate,
  ];

  if (isDateFilter && isDateFilter === "true") {
    queryKeys.push(isDateFilter);
  }

  if (resetFilter && resetFilter === "true") {
    queryKeys.push(resetFilter);
  }

  return useQuery({
    queryKey: queryKeys,
    queryFn: () => getIndividualVaultSavingsStats(payload),
    enabled: savingsType && savingsType === IndividualSavingsTabStatus.VAULT,
  });
};

export const useGetIndividualVaultSavings = () => {
  const [searchParams] = useSearchParams();
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");
  const search = searchParams.get("search");
  const page = searchParams.get("page");
  const limit = searchParams.get("limit");
  const resetFilter = searchParams.get("resetFilter");
  const status = searchParams.get("status");
  const lockDuration = searchParams.get("lockDuration");
  const filterRetryCount = searchParams.get("filterRetryCount");
  const tableRefreshCount = searchParams.get("tableRefreshCount");
  const payload: IGetIndividualVaultSavingsPayload = {
    page: page || String(PAGE_ONE),
    limit: limit || String(DEFAULT_PAGINATION_LIMIT),
  };

  const queryKeys = [
    IndividualSavingsQueryKeys.GET_INDIVIDUAL_VAULT_SAVINGS,
    payload.page,
    payload.limit,
  ];
  if (startDate) {
    payload["startDate"] = startDate;
    queryKeys.push(startDate);
  }

  if (endDate) {
    payload["endDate"] = endDate;
    queryKeys.push(endDate);
  }

  if (startDate && !endDate) {
    const newEndDate = getTodayAsEndDateFilterValue();
    payload["endDate"] = newEndDate;
    queryKeys.push(newEndDate);
  }

  if (search) {
    payload["search"] = search;
    payload["searchBy"] = "name";
    queryKeys.push(search);
  }

  if (status) {
    const vaultSavingsStatuses = status.split(",");
    if (!vaultSavingsStatuses.includes("all")) {
      payload["status"] = vaultSavingsStatuses.join(",");
    }
    queryKeys.push(status);
  }

  if (lockDuration) {
    payload["lockDuration"] = lockDuration;
    queryKeys.push(lockDuration);
  }

  if (filterRetryCount) {
    queryKeys.push(filterRetryCount);
  }

  if (resetFilter) {
    queryKeys.push(resetFilter);
  }

  if (tableRefreshCount && tableRefreshCount?.length > ZERO) {
    payload["page"] = String(PAGE_ONE);
    payload["limit"] = String(DEFAULT_PAGINATION_LIMIT);
  }

  return useQuery({
    queryKey: queryKeys,
    queryFn: () => getIndividualVaultSavings(payload),
    retry: false,
  });
};

export const useGetIndividualVaultSavingsDetails = () => {
  const { id: slug } = useParams();
  const vaultId = slug || "";
  const queryKeys = [
    IndividualSavingsQueryKeys.GET_INDIVIDUAL_VAULT_SAVINGS_DETAILS,
    vaultId,
  ];
  const payload: IGetIndividualVaultSavingsDetailsPayload = {
    vaultId,
  };

  return useQuery({
    queryKey: queryKeys,
    queryFn: () => getIndividualVaultSavingsDetails(payload),
    retry: false,
  });
};

export const useGetIndividualVaultSavingsTransactions = () => {
  const { id: slug } = useParams();
  const vaultId = slug || "";
  const [searchParams] = useSearchParams();
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");
  const search = searchParams.get("search");
  const page = searchParams.get("page");
  const limit = searchParams.get("limit");
  const resetFilter = searchParams.get("resetFilter");
  const filterRetryCount = searchParams.get("filterRetryCount");
  const tableRefreshCount = searchParams.get("tableRefreshCount");
  const payload: IGetIndividualVaultSavingsTransactionsPayload = {
    page: page || String(PAGE_ONE),
    limit: limit || String(DEFAULT_PAGINATION_LIMIT),
    vaultId,
  };

  const queryKeys = [
    IndividualSavingsQueryKeys.GET_INDIVIDUAL_VAULT_SAVINGS_TRANSACTIONS,
    payload.page,
    payload.limit,
    vaultId,
  ];
  if (startDate) {
    payload["startDate"] = startDate;
    queryKeys.push(startDate);
  }

  if (endDate) {
    payload["endDate"] = endDate;
    queryKeys.push(endDate);
  }

  if (startDate && !endDate) {
    const newEndDate = getTodayAsEndDateFilterValue();
    payload["endDate"] = newEndDate;
    queryKeys.push(newEndDate);
  }

  if (search) {
    payload["search"] = search;
    payload["searchBy"] = "reference";
    queryKeys.push(search);
  }

  if (filterRetryCount) {
    queryKeys.push(filterRetryCount);
  }

  if (resetFilter) {
    queryKeys.push(resetFilter);
  }

  if (tableRefreshCount && tableRefreshCount?.length > ZERO) {
    payload["page"] = String(PAGE_ONE);
    payload["limit"] = String(DEFAULT_PAGINATION_LIMIT);
  }

  return useQuery({
    queryKey: queryKeys,
    queryFn: () => getIndividualVaultSavingsTransactions(payload),
    retry: false,
  });
};

export const useGetIndividualVaultSavingsTransactionsStats = () => {
  const { id: slug } = useParams();
  const vaultId = slug || "";
  const [searchParams] = useSearchParams();
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");
  const isDateFilter = searchParams.get("isDateFilter");
  const resetFilter = searchParams.get("resetFilter");
  const payload: IGetIndividualVaultSavingsTransactionsStatsPayload = {
    vaultId,
  };

  if (isDateFilter && isDateFilter === "true") {
    if (startDate) {
      payload["startDate"] = startDate;
    }

    if (endDate) {
      payload["endDate"] = endDate;
    }
  }

  const queryKeys = [
    IndividualSavingsQueryKeys.GET_INDIVIDUAL_VAULT_SAVINGS_TRANSACTIONS_STATS,
    payload.vaultId,
    payload?.startDate,
    payload?.endDate,
  ];

  if (isDateFilter && isDateFilter === "true") {
    queryKeys.push(isDateFilter);
  }

  if (resetFilter && resetFilter === "true") {
    queryKeys.push(resetFilter);
  }

  return useQuery({
    queryKey: queryKeys,
    queryFn: () => getIndividualVaultSavingsTransactionsStats(payload),
  });
};

export const useGetIndividualSavingsCount = () => {
  const queryKeys = [IndividualSavingsQueryKeys.GET_INDIVIDUAL_SAVINGS_COUNT];

  return useQuery({
    queryKey: queryKeys,
    queryFn: () => getIndividualSavingsCount(),
  });
};
